import { DEFAULT_NOTE_TYPES, type ImportPreview, type ImportReport } from '@kartothek/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { t } from '../i18n';

type FolderCfg = Record<string, { typeKey: string; visibility: 'dm' | 'all' | 'players' }>;

const TYPE_KEYS = DEFAULT_NOTE_TYPES.map((x) => x.key);

export function ImportPage() {
  const { cid } = useParams();
  const qc = useQueryClient();
  const campaigns = useQuery({ queryKey: ['campaigns'], queryFn: api.campaigns });
  const types = useQuery({ queryKey: ['types', cid], queryFn: () => api.types(cid!), enabled: !!cid });
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [cfg, setCfg] = useState<FolderCfg>({});
  const [target, setTarget] = useState<'new' | string>(cid ?? 'new');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);

  const typeLabel = (key: string) =>
    types.data?.find((x) => x.key === key)?.label ?? DEFAULT_NOTE_TYPES.find((x) => x.key === key)?.label ?? key;

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setReport(null);
    try {
      const p = await api.importPreview(file);
      setPreview(p);
      setNewName(p.vaultName);
      setCfg(Object.fromEntries(p.folders.map((f) => [f.folder, { typeKey: f.suggestedTypeKey, visibility: f.suggestedVisibility }])));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.importCommit({
        token: preview.token,
        folders: cfg,
        ...(target === 'new' ? { newCampaignName: newName } : { campaignId: target }),
      });
      setReport(r);
      setPreview(null);
      await qc.invalidateQueries();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-narrow">
      {!cid && (
        <p>
          <Link to="/">← {t('campaigns.title')}</Link>
        </p>
      )}
      <h1>{t('import.title')}</h1>
      <p className="muted">{t('import.intro')}</p>

      {!preview && (
        <label className="file-drop">
          <input type="file" accept=".zip,application/zip" disabled={busy} onChange={(e) => onFile(e.target.files?.[0])} />
          {busy ? t('import.analyzing') : t('import.choose')}
        </label>
      )}
      {error && <div className="error banner">{error}</div>}

      {preview && (
        <section className="card">
          <h2>{t('import.preview')}</h2>
          <p>
            {t('import.vault')}: <strong>{preview.vaultName}</strong> · {preview.noteCount} {t('import.notes')} · {preview.fileCount}{' '}
            {t('import.files')}
            {preview.homeNote && (
              <>
                {' '}
                · {t('import.homeNote')}: <em>{preview.homeNote}</em>
              </>
            )}
          </p>
          <table className="table">
            <thead>
              <tr>
                <th>{t('import.folder')}</th>
                <th>{t('import.notes')}</th>
                <th>{t('note.type')}</th>
                <th>{t('note.visibility')}</th>
              </tr>
            </thead>
            <tbody>
              {preview.folders.map((f) => (
                <tr key={f.folder}>
                  <td>
                    {f.color && <span className="type-dot" style={{ background: f.color }} />}
                    {f.folder || t('nav.root')}
                  </td>
                  <td>{f.noteCount}</td>
                  <td>
                    <select
                      value={cfg[f.folder]?.typeKey}
                      onChange={(e) => setCfg({ ...cfg, [f.folder]: { ...cfg[f.folder]!, typeKey: e.target.value } })}
                    >
                      {TYPE_KEYS.map((k) => (
                        <option key={k} value={k}>
                          {typeLabel(k)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={cfg[f.folder]?.visibility}
                      onChange={(e) =>
                        setCfg({ ...cfg, [f.folder]: { ...cfg[f.folder]!, visibility: e.target.value as 'dm' | 'all' } })
                      }
                    >
                      <option value="dm">{t('visibility.dm')}</option>
                      <option value="all">{t('visibility.all')}</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {preview.skipped.length > 0 && (
            <details>
              <summary>
                {t('import.skipped')} ({preview.skipped.length})
              </summary>
              <ul className="small">
                {preview.skipped.map((s) => (
                  <li key={s.path}>
                    {s.path} – <span className="muted">{s.reason}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
          <div className="row-form">
            <label>
              {t('import.target')}
              <select value={target} onChange={(e) => setTarget(e.target.value)}>
                <option value="new">{t('import.newCampaign')}</option>
                {campaigns.data
                  ?.filter((c) => c.role !== 'player')
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </label>
            {target === 'new' && (
              <label>
                {t('common.name')}
                <input value={newName} onChange={(e) => setNewName(e.target.value)} />
              </label>
            )}
          </div>
          <div className="modal-actions">
            <button onClick={() => setPreview(null)}>{t('common.cancel')}</button>
            <button className="primary" disabled={busy || (target === 'new' && !newName.trim())} onClick={commit}>
              {busy ? t('import.running') : t('import.start')}
            </button>
          </div>
        </section>
      )}

      {report && <ImportReportView report={report} />}
    </div>
  );
}

function ImportReportView({ report }: { report: ImportReport }) {
  const n = report.notes;
  const f = report.files;
  return (
    <section className="card report">
      <h2>{t('import.report')}</h2>
      <ul>
        <li>
          <strong>{n.total}</strong> {t('import.notes')}: {n.created} {t('import.created')}, {n.updated} {t('import.updated')}, {n.unchanged}{' '}
          {t('import.unchanged')}
        </li>
        <li>
          <strong>{f.total}</strong> {t('import.files')}: {f.created} {t('import.created')}, {f.updated} {t('import.updated')}, {f.unchanged}{' '}
          {t('import.unchanged')}
        </li>
        <li>
          <strong>{report.links.total}</strong> {t('import.links')}: {report.links.resolved} {t('import.resolved')}, {report.links.unresolved}{' '}
          {t('import.openLinks').toLowerCase()}
        </li>
      </ul>
      {report.openLinks.length > 0 && (
        <details open>
          <summary>
            {t('import.openLinks')} ({report.openLinks.length})
          </summary>
          <ul className="small">
            {report.openLinks.map((l, i) => (
              <li key={i}>
                <span className="wikilink unresolved">{l.target}</span> <span className="muted">← {l.from}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {report.conflicts.length > 0 && (
        <details open>
          <summary>
            {t('import.conflicts')} ({report.conflicts.length})
          </summary>
          <ul className="small">
            {report.conflicts.map((c, i) => (
              <li key={i}>
                [[{c.target}]] in {c.from}: {t('import.chosen')} <strong>{c.chosen}</strong>{' '}
                <span className="muted">({c.candidates.join(' | ')})</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {report.skipped.length > 0 && (
        <details>
          <summary>
            {t('import.skipped')} ({report.skipped.length})
          </summary>
          <ul className="small">
            {report.skipped.map((s) => (
              <li key={s.path}>
                {s.path} – <span className="muted">{s.reason}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {report.notInVault.length > 0 && (
        <details>
          <summary>
            {t('import.notInVault')} ({report.notInVault.length})
          </summary>
          <ul className="small">
            {report.notInVault.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </details>
      )}
      {report.warnings.length > 0 && (
        <details>
          <summary>
            {t('import.warnings')} ({report.warnings.length})
          </summary>
          <ul className="small">
            {report.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </details>
      )}
      <p>
        <Link className="button primary" to={`/c/${report.campaignId}`}>
          {t('import.open')} →
        </Link>
      </p>
    </section>
  );
}
